import "mapbox-gl/dist/mapbox-gl.css";
import { waitForRenderableContainerAsync } from "../map-container";
import { IMapInstance, IMapProvider, MapInitOptions } from "../map-provider.interface";
import { MapboxMapInstance, toMapboxZoom } from "./mapbox-map-instance";
import { waitForStyleLoadedAsync } from "./mapbox-style-loaded";

export type MapboxProviderOptions = {
    apiKey: string;
};

export type MapboxModule = typeof import("mapbox-gl")["default"];

export class MapboxProvider implements IMapProvider {
    private static _mapboxPromise: Promise<MapboxModule> | null = null;

    // Callers rebuild the map on the same container without tearing the old one down, and each
    // Mapbox map holds a WebGL context browsers cap, so the previous one is disposed first.
    private static readonly _disposeByContainer = new WeakMap<HTMLElement, () => void>();

    private readonly _apiKey: string;

    constructor(options: MapboxProviderOptions) {
        this._apiKey = options.apiKey;
    }

    async createMap(container: HTMLElement, options: MapInitOptions): Promise<IMapInstance> {
        await waitForRenderableContainerAsync(container);
        const mapboxgl = await this._getMapboxAsync();

        MapboxProvider._disposeByContainer.get(container)?.();

        const map = new mapboxgl.Map({
            container,
            accessToken: this._apiKey,
            style: "mapbox://styles/mapbox/streets-v12",
            // v3 defaults to a 3D globe when zoomed out; the shared bounds logic assumes a flat map like Google/HERE.
            projection: "mercator",
            center: [options.center.lng, options.center.lat],
            zoom: toMapboxZoom(options.zoom),
            cooperativeGestures: options.gestureHandling === "cooperative"
        });

        const resizeObserver = new ResizeObserver(() => map.resize());
        resizeObserver.observe(container);

        MapboxProvider._disposeByContainer.set(container, () => {
            resizeObserver.disconnect();
            map.remove();
        });

        // Starts now so an early style error is caught; only route drawing awaits it, so a handler
        // keeps a failed style from also logging as an unhandled rejection.
        const styleLoaded = waitForStyleLoadedAsync(map);
        styleLoaded.catch(() => undefined);

        return new MapboxMapInstance(mapboxgl, map, this._apiKey, styleLoaded);
    }

    // Lazy-loads the npm package, not a local .ts wrapper: expose-loader throws on .ts modules evaluated after startup.
    private async _getMapboxAsync(): Promise<MapboxModule> {
        MapboxProvider._mapboxPromise ??= import(
            /* webpackChunkName: "mapbox-gl" */
            "mapbox-gl"
        ).then(module => module.default);

        return MapboxProvider._mapboxPromise;
    }
}
